import { useEffect, useRef, useCallback } from 'react';
import * as echarts from 'echarts';

/**
 * Decode HTML entities that browsers inject into attribute values.
 * e.g. &quot; → ", &amp; → &, &#39; → '
 */
function decodeHtmlEntities(str) {
  const el = document.createElement('textarea');
  el.innerHTML = str;
  return el.value;
}

/**
 * Try to parse a data-echarts attribute value as JSON.
 * Handles HTML-entity-encoded JSON and both quote styles.
 */
function parseEchartsAttr(div) {
  const raw = div.getAttribute('data-echarts');
  if (!raw) return null;

  // Try raw first
  try { return JSON.parse(raw); } catch {}

  // Try after decoding HTML entities
  try { return JSON.parse(decodeHtmlEntities(raw)); } catch {}

  // Try replacing HTML entities manually
  try {
    const cleaned = raw
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&#39;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
    return JSON.parse(cleaned);
  } catch {}

  return null;
}

export function ReportPage({ html, pageNumber, totalPages, onContentChange }) {
  const containerRef = useRef(null);
  const chartsRef = useRef([]);

  const initCharts = useCallback(() => {
    if (!containerRef.current) return;

    // Dispose previous
    chartsRef.current.forEach(c => {
      try { c.dispose(); } catch {}
    });
    chartsRef.current = [];

    // Find all chart placeholders
    const chartDivs = containerRef.current.querySelectorAll('[data-echarts]');
    chartDivs.forEach(div => {
      const option = parseEchartsAttr(div);
      if (!option) {
        console.warn('Failed to parse data-echarts:', div.getAttribute('data-echarts')?.substring(0, 100));
        return;
      }

      // Ensure the div has a minimum height for ECharts to render
      if (!div.style.height || div.style.height === '0px') {
        div.style.height = '300px';
      }
      div.style.width = div.style.width || '100%';

      try {
        const chart = echarts.init(div, null, { renderer: 'canvas' });
        chart.setOption(option);
        chartsRef.current.push(chart);

        // Expose echarts globally for print conversion
        if (!window.echarts) window.echarts = echarts;
      } catch (e) {
        console.warn('Failed to init ECharts:', e);
      }
    });
  }, []);

  // Initialize charts after HTML is injected into the DOM
  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM has settled after dangerouslySetInnerHTML
    const raf = requestAnimationFrame(() => {
      // Additional delay to let browser fully parse and render the injected HTML
      setTimeout(initCharts, 50);
    });

    return () => {
      cancelAnimationFrame(raf);
      chartsRef.current.forEach(c => {
        try { c.dispose(); } catch {}
      });
      chartsRef.current = [];
    };
  }, [html, initCharts]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => chartsRef.current.forEach(c => {
      try { c.resize(); } catch {}
    });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div
      className="report-page-wrapper mb-10 shadow-lg rounded-sm border border-zinc-200 print:mb-0 print:shadow-none print:border-0 print:rounded-none"
      style={{ maxWidth: '210mm', margin: '0 auto 40px' }}
    >
      <div
        ref={containerRef}
        className="report-page-content"
        contentEditable
        suppressContentEditableWarning
        dangerouslySetInnerHTML={{ __html: html }}
        onBlur={() => {
          if (onContentChange && containerRef.current) {
            onContentChange(containerRef.current.innerHTML);
          }
        }}
        style={{
          outline: 'none',
          minHeight: '297mm',
        }}
      />
    </div>
  );
}
